import React from "react";
import NoData from "../assets/images/NoDataTableImage.svg";

export type YhTableCellParams = {
  row: any;
  value: any;
  field: string;
};

export type YhTableColumn = {
  field: string;
  headerName: React.ReactNode;
  minWidth?: number;
  width?: number;
  flex?: number;
  renderCell?: (params: YhTableCellParams) => React.ReactNode;
};

export type YhTableProps = {
  columns: YhTableColumn[];
  rows: any[];
  getRowId?: (row: any) => string | number;
  rowHeight?: number;
  loading?: boolean;
  emptyMessage?: string;
  onRowClick?: (row: any, event: React.MouseEvent) => void;
  toolbar?: React.ReactNode;
  /** 0-indexed page */
  page?: number;
  pageSize?: number;
  totalRows?: number;
  pageSizeOptions?: number[];
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  className?: string;
  showPagination?: boolean;
  /** Label used in "Showing X to Y of Z {label}" (default: leads) */
  paginationLabel?: string;
  /** Optional max height for the scrollable body (e.g. "420px" or "50vh") */
  maxBodyHeight?: string | number;
};

const defaultGetRowId = (row: any) => row._id ?? row.id;

export const YhTable: React.FC<YhTableProps> = ({
  columns,
  rows,
  getRowId = defaultGetRowId,
  rowHeight = 76,
  loading = false,
  emptyMessage = "No data found",
  onRowClick,
  toolbar,
  page = 0,
  pageSize = 25,
  totalRows,
  pageSizeOptions = [10, 25, 50, 100, 200],
  onPageChange,
  onPageSizeChange,
  className = "",
  showPagination = true,
  paginationLabel = "leads",
  maxBodyHeight,
}) => {
  const total = totalRows ?? rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);

  const pageNumbers = (() => {
    const maxButtons = 5;
    if (pageCount <= maxButtons) {
      return Array.from({ length: pageCount }, (_, i) => i);
    }
    let start = Math.max(0, page - 2);
    let end = Math.min(pageCount - 1, start + maxButtons - 1);
    start = Math.max(0, end - maxButtons + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  return (
    <div
      className={`flex h-full min-h-0 w-full flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)] ${className}`}
    >
      {toolbar && (
        <div className="shrink-0 border-b border-[#EEF0F5] px-4 py-3">
          {toolbar}
        </div>
      )}

      <div
        className="min-h-0 w-full flex-1 overflow-auto [scrollbar-width:thin]"
        style={
          maxBodyHeight
            ? { maxHeight: maxBodyHeight, flex: "none" }
            : undefined
        }
      >
        <table className="w-full min-w-[980px] border-collapse text-left">
          <thead className="sticky top-0 z-[1]">
            <tr className="border-b border-[#EEF0F5] bg-white">
              {columns.map((col) => (
                <th
                  key={col.field}
                  className="whitespace-nowrap bg-white px-4 py-3.5 text-xs font-semibold uppercase tracking-wide text-[#6B7280]"
                  style={{
                    minWidth: col.minWidth,
                    width: col.width,
                  }}
                >
                  {col.headerName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`} className="border-b border-[#F3F4F6]">
                  {columns.map((col) => (
                    <td key={col.field} className="px-4 py-4">
                      <div className="h-4 w-3/4 animate-pulse rounded-lg bg-[#F3F4F6]" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-16 text-center">
                  <img
                    src={NoData}
                    alt={emptyMessage}
                    className="mx-auto mb-3 h-20 w-auto"
                  />
                  <p className="text-sm text-[#9CA3AF]">{emptyMessage}</p>
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const id = getRowId(row);
                return (
                  <tr
                    key={String(id)}
                    className={`border-b border-[#F3F4F6] transition-colors hover:bg-[#F8FAFC] ${onRowClick ? "cursor-pointer" : ""
                      }`}
                    style={{ height: rowHeight }}
                    onClick={(e) => onRowClick?.(row, e)}
                  >
                    {columns.map((col) => {
                      const value = row?.[col.field];
                      return (
                        <td
                          key={col.field}
                          className="px-4 py-3 align-middle text-sm text-[#111827]"
                          style={{ minWidth: col.minWidth, width: col.width }}
                        >
                          {col.renderCell
                            ? col.renderCell({ row, value, field: col.field })
                            : value ?? "—"}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-[#EEF0F5] bg-white px-4 py-3 text-sm text-[#6B7280]">
          <span>
            Showing{" "}
            <span className="font-semibold text-[#111827]">{from}</span> to{" "}
            <span className="font-semibold text-[#111827]">{to}</span> of{" "}
            <span className="font-semibold text-[#111827]">
              {total.toLocaleString()}
            </span>{" "}
            {paginationLabel}
          </span>

          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs">Rows per page</span>
            <select
              className="rounded-xl border border-[#E5E7EB] bg-white px-2.5 py-1.5 text-xs outline-none focus:border-[#A02448]"
              value={pageSize}
              onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>

            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page <= 0}
                className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-[#E5E7EB] disabled:opacity-40 hover:bg-[#F3F4F6]"
                onClick={() => onPageChange?.(Math.max(0, page - 1))}
                aria-label="Previous page"
              >
                ‹
              </button>
              {pageNumbers.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => onPageChange?.(p)}
                  className={`inline-flex h-8 min-w-[32px] items-center justify-center rounded-full px-2 text-xs font-semibold transition ${p === page
                      ? "bg-[#A02448] text-white"
                      : "text-[#374151] hover:bg-[#F3F4F6]"
                    }`}
                >
                  {p + 1}
                </button>
              ))}
              <button
                type="button"
                disabled={page + 1 >= pageCount}
                className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-[#E5E7EB] disabled:opacity-40 hover:bg-[#F3F4F6]"
                onClick={() => onPageChange?.(Math.min(pageCount - 1, page + 1))}
                aria-label="Next page"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YhTable;
